import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useAuth } from '@/App';
import { useWebSocket } from '@/hooks/useRideTracking';
import { toast } from 'sonner';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Status colors
const STATUS_COLORS = {
  pending: 'bg-amber-500',
  accepted: 'bg-blue-500',
  arrived_pickup: 'bg-purple-500',
  package_picked: 'bg-cyan-500',
  arrived_delivery: 'bg-emerald-500',
  delivered: 'bg-green-500',
  cancelled: 'bg-red-500'
};

// Status labels
const STATUS_LABELS = {
  pending: 'Finding Driver',
  accepted: 'Driver Assigned',
  arrived_pickup: 'Driver Arrived at Pickup',
  package_picked: 'Package Picked Up',
  arrived_delivery: 'At Delivery Location',
  delivered: 'Delivered',
  cancelled: 'Cancelled'
};

// Map center updater component
const MapCenterUpdater = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, 15);
    }
  }, [center, map]);
  return null;
};

const PackageDeliveryTracking = ({ delivery, onClose, onRefresh }) => {
  const { user } = useAuth();
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [driverLocation, setDriverLocation] = useState(null);
  
  const { isConnected, subscribe, sendMessage } = useWebSocket(user?.id);

  // Fetch tracking data
  const fetchTracking = useCallback(async () => {
    if (!delivery?.id) return;
    try {
      const res = await axios.get(`${API}/package/${delivery.id}/tracking`);
      setTracking(res.data);
      if (res.data.driver_location) {
        setDriverLocation(res.data.driver_location);
      }
    } catch (err) {
      console.error('Failed to fetch tracking:', err);
    } finally {
      setLoading(false);
    }
  }, [delivery?.id]);

  useEffect(() => {
    fetchTracking();
    const interval = setInterval(fetchTracking, 10000); // Poll every 10 seconds
    return () => clearInterval(interval);
  }, [fetchTracking]);

  // Subscribe to WebSocket updates
  useEffect(() => {
    if (!isConnected || !delivery?.id) return;

    // Subscribe to this delivery's updates
    sendMessage({
      type: 'subscribe_ride',
      ride_id: delivery.id
    });

    const unsubscribeLocation = subscribe('driver_location_update', (data) => {
      if (data.ride_id === delivery.id && data.location) {
        setDriverLocation(data.location);
      }
    });

    const unsubscribeStatus = subscribe('package_status_update', (data) => {
      if (data.delivery_id === delivery.id) {
        setTracking(prev => ({
          ...prev,
          status: data.status,
          status_info: data.status_info
        }));
        toast.success(`Package status: ${data.status_info?.label || data.status}`);
        if (onRefresh) onRefresh();
      }
    });

    const unsubscribeDelivered = subscribe('package_delivered', (data) => {
      if (data.delivery_id === delivery.id) {
        toast.success('Your package has been delivered!');
        fetchTracking();
        if (onRefresh) onRefresh();
      }
    });

    return () => {
      unsubscribeLocation?.();
      unsubscribeStatus?.();
      unsubscribeDelivered?.();
    };
  }, [isConnected, delivery?.id, subscribe, sendMessage, fetchTracking, onRefresh]);

  // Custom marker icons
  const pickupIcon = new L.DivIcon({
    className: 'custom-marker',
    html: '<div class="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold shadow-lg border-2 border-white">P</div>',
    iconSize: [32, 32],
    iconAnchor: [16, 32]
  });

  const dropIcon = new L.DivIcon({
    className: 'custom-marker',
    html: '<div class="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white font-bold shadow-lg border-2 border-white">D</div>',
    iconSize: [32, 32],
    iconAnchor: [16, 32]
  });

  const driverIcon = new L.DivIcon({
    className: 'custom-marker',
    html: '<div class="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center shadow-lg border-2 border-white animate-pulse">🏍️</div>',
    iconSize: [40, 40],
    iconAnchor: [20, 40]
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const status = tracking?.status || delivery?.package_status || 'pending';
  const statusInfo = tracking?.status_info || {};

  return (
    <div className="space-y-4">
      {/* Status Banner */}
      <div className={`p-4 rounded-xl text-white ${STATUS_COLORS[status] || 'bg-gray-500'}`}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold">{statusInfo.label || STATUS_LABELS[status]}</h3>
            <p className="text-sm opacity-90">{statusInfo.description}</p>
          </div>
          <Badge variant="secondary" className="text-xs">
            {tracking?.is_driver_online ? '🟢 Live' : '⚪ Offline'}
          </Badge>
        </div>
      </div>

      {/* Map */}
      <div className="h-64 rounded-xl overflow-hidden border border-border">
        <MapContainer
          center={[delivery?.pickup?.lat || 12.9716, delivery?.pickup?.lng || 77.5946]}
          zoom={14}
          className="h-full w-full"
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; OpenStreetMap'
          />
          
          {/* Pickup marker */}
          {delivery?.pickup && (
            <Marker position={[delivery.pickup.lat, delivery.pickup.lng]} icon={pickupIcon}>
              <Popup>
                <strong>Pickup</strong><br />
                {delivery.pickup.address}
              </Popup>
            </Marker>
          )}
          
          {/* Drop marker */}
          {delivery?.drop && (
            <Marker position={[delivery.drop.lat, delivery.drop.lng]} icon={dropIcon}>
              <Popup>
                <strong>Delivery</strong><br />
                {delivery.drop.address}
              </Popup>
            </Marker>
          )}
          
          {/* Driver marker */}
          {driverLocation && (
            <Marker position={[driverLocation.lat, driverLocation.lng]} icon={driverIcon}>
              <Popup>
                <strong>Driver</strong><br />
                {tracking?.driver?.name || 'En route'}
              </Popup>
            </Marker>
          )}
          
          {/* Route line */}
          {delivery?.pickup && delivery?.drop && (
            <Polyline
              positions={[
                [delivery.pickup.lat, delivery.pickup.lng],
                ...(driverLocation ? [[driverLocation.lat, driverLocation.lng]] : []),
                [delivery.drop.lat, delivery.drop.lng]
              ]}
              color="#3b82f6"
              weight={3}
              dashArray="10, 10"
            />
          )}
          
          {driverLocation && <MapCenterUpdater center={[driverLocation.lat, driverLocation.lng]} />}
        </MapContainer>
      </div>

      {/* Status Timeline */}
      <Card className="p-4">
        <h4 className="font-semibold mb-3">Delivery Progress</h4>
        <div className="space-y-3">
          {tracking?.status_history?.map((item, idx) => (
            <div key={idx} className="flex items-start gap-3">
              <div className={`w-3 h-3 rounded-full mt-1.5 ${
                idx === tracking.status_history.length - 1 ? STATUS_COLORS[item.status] : 'bg-gray-300'
              }`}></div>
              <div className="flex-1">
                <p className="font-medium text-sm">{STATUS_LABELS[item.status] || item.status}</p>
                <p className="text-xs text-muted-foreground">{item.notes}</p>
                <p className="text-xs text-muted-foreground">
                  {new Date(item.timestamp).toLocaleTimeString()}
                </p>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Driver Info */}
      {tracking?.driver && (
        <Card className="p-4">
          <h4 className="font-semibold mb-3">Driver Details</h4>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
              🏍️
            </div>
            <div className="flex-1">
              <p className="font-medium">{tracking.driver.name}</p>
              <p className="text-sm text-muted-foreground">{tracking.driver.vehicle_number}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(`tel:${tracking.driver.phone}`)}
            >
              📞 Call
            </Button>
          </div>
        </Card>
      )}

      {/* Package Info */}
      <Card className="p-4">
        <h4 className="font-semibold mb-3">Package Details</h4>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-muted-foreground">Weight</p>
            <p className="font-medium">{delivery?.package?.weight_kg || 1} kg</p>
          </div>
          <div>
            <p className="text-muted-foreground">Distance</p>
            <p className="font-medium">{delivery?.distance_km} km</p>
          </div>
          <div>
            <p className="text-muted-foreground">Recipient</p>
            <p className="font-medium">{delivery?.package?.recipient_name || 'N/A'}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Fare</p>
            <p className="font-medium">₹{delivery?.fare}</p>
          </div>
        </div>
        {delivery?.package?.description && (
          <div className="mt-3 pt-3 border-t">
            <p className="text-muted-foreground text-sm">Description</p>
            <p className="text-sm">{delivery.package.description}</p>
          </div>
        )}
      </Card>

      {/* OTPs for pickup/delivery */}
      {['pending', 'accepted', 'arrived_pickup'].includes(status) && delivery?.pickup_otp && (
        <Card className="p-4 bg-green-50 dark:bg-green-900/20 border-green-200">
          <p className="text-sm text-green-700 dark:text-green-300 mb-1">Pickup OTP (Share with driver)</p>
          <p className="text-2xl font-bold tracking-widest text-green-800 dark:text-green-200">{delivery.pickup_otp}</p>
        </Card>
      )}
      
      {['package_picked', 'arrived_delivery'].includes(status) && delivery?.delivery_otp && (
        <Card className="p-4 bg-blue-50 dark:bg-blue-900/20 border-blue-200">
          <p className="text-sm text-blue-700 dark:text-blue-300 mb-1">Delivery OTP (Share with recipient)</p>
          <p className="text-2xl font-bold tracking-widest text-blue-800 dark:text-blue-200">{delivery.delivery_otp}</p>
        </Card>
      )}
    </div>
  );
};

// Main Package Delivery Component
const PackageDelivery = () => {
  const { user } = useAuth();
  const [activeDeliveries, setActiveDeliveries] = useState([]);
  const [deliveryHistory, setDeliveryHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [showNewDelivery, setShowNewDelivery] = useState(false);
  const [bookingPricing, setBookingPricing] = useState(null);
  
  // Form state for new delivery
  const [newDelivery, setNewDelivery] = useState({
    pickupAddress: '',
    pickupLat: null,
    pickupLng: null,
    deliveryAddress: '',
    deliveryLat: null,
    deliveryLng: null,
    weight: 1,
    description: '',
    recipientName: '',
    recipientPhone: '',
    instructions: ''
  });
  const [fareEstimate, setFareEstimate] = useState(null);

  const fetchActiveDeliveries = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await axios.get(`${API}/package/customer/${user.id}/active`);
      setActiveDeliveries(res.data);
    } catch (err) {
      console.error('Failed to fetch active deliveries:', err);
    }
  }, [user?.id]);

  const fetchDeliveryHistory = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await axios.get(`${API}/package/customer/${user.id}/history`);
      setDeliveryHistory(res.data);
    } catch (err) {
      console.error('Failed to fetch delivery history:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  const fetchBookingPricing = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/vehicles/bike/booking-pricing`);
      setBookingPricing(res.data);
    } catch (err) {
      console.error('Failed to fetch pricing:', err);
    }
  }, []);

  useEffect(() => {
    fetchActiveDeliveries();
    fetchDeliveryHistory();
    fetchBookingPricing();
  }, [fetchActiveDeliveries, fetchDeliveryHistory, fetchBookingPricing]);

  const handleRefresh = () => {
    fetchActiveDeliveries();
    fetchDeliveryHistory();
  };

  const estimateFare = async () => {
    if (!newDelivery.pickupLat || !newDelivery.deliveryLat) {
      toast.error('Please select pickup and delivery locations');
      return;
    }

    try {
      const res = await axios.post(`${API}/fare/estimate`, {
        pickup: { lat: newDelivery.pickupLat, lng: newDelivery.pickupLng },
        dropoff: { lat: newDelivery.deliveryLat, lng: newDelivery.deliveryLng },
        vehicle_type: 'bike',
        booking_type: 'package',
        package_weight_kg: newDelivery.weight
      });
      setFareEstimate(res.data);
    } catch (err) {
      toast.error('Failed to estimate fare');
    }
  };

  const createDelivery = async () => {
    if (!fareEstimate) {
      toast.error('Please estimate fare first');
      return;
    }

    try {
      const res = await axios.post(`${API}/package`, {
        customer_id: user.id,
        pickup_lat: newDelivery.pickupLat,
        pickup_lng: newDelivery.pickupLng,
        pickup_address: newDelivery.pickupAddress,
        delivery_lat: newDelivery.deliveryLat,
        delivery_lng: newDelivery.deliveryLng,
        delivery_address: newDelivery.deliveryAddress,
        vehicle_type: 'bike',
        package_weight_kg: newDelivery.weight,
        package_description: newDelivery.description,
        recipient_name: newDelivery.recipientName,
        recipient_phone: newDelivery.recipientPhone,
        special_instructions: newDelivery.instructions,
        fare: fareEstimate.fare
      });

      toast.success('Package delivery request created!');
      setShowNewDelivery(false);
      setNewDelivery({
        pickupAddress: '', pickupLat: null, pickupLng: null,
        deliveryAddress: '', deliveryLat: null, deliveryLng: null,
        weight: 1, description: '', recipientName: '', recipientPhone: '', instructions: ''
      });
      setFareEstimate(null);
      fetchActiveDeliveries();
      setSelectedDelivery(res.data);
    } catch (err) {
      toast.error('Failed to create delivery request');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Package Delivery</h2>
          <p className="text-muted-foreground">Send packages with real-time tracking</p>
        </div>
        <Button onClick={() => setShowNewDelivery(true)} data-testid="new-delivery-btn">
          📦 New Delivery
        </Button>
      </div>

      {/* Active Deliveries */}
      {activeDeliveries.length > 0 && (
        <div>
          <h3 className="font-semibold mb-3">Active Deliveries</h3>
          <div className="grid gap-3">
            {activeDeliveries.map((delivery) => (
              <Card
                key={delivery.id}
                className="p-4 cursor-pointer hover:border-primary transition-colors"
                onClick={() => setSelectedDelivery(delivery)}
                data-testid={`active-delivery-${delivery.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={STATUS_COLORS[delivery.package_status || delivery.status]}>
                        {STATUS_LABELS[delivery.package_status || delivery.status]}
                      </Badge>
                      {delivery.driver_name && (
                        <span className="text-sm text-muted-foreground">• {delivery.driver_name}</span>
                      )}
                    </div>
                    <p className="text-sm">
                      📍 {delivery.pickup?.address?.substring(0, 30)}...
                    </p>
                    <p className="text-sm">
                      📦 {delivery.drop?.address?.substring(0, 30)}...
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold">₹{delivery.fare}</p>
                    <p className="text-xs text-muted-foreground">{delivery.distance_km} km</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* No Active Deliveries */}
      {!loading && activeDeliveries.length === 0 && (
        <Card className="p-8 text-center">
          <div className="text-4xl mb-3">📦</div>
          <h3 className="font-semibold mb-1">No Active Deliveries</h3>
          <p className="text-muted-foreground mb-4">Send a package with real-time tracking</p>
          <Button onClick={() => setShowNewDelivery(true)}>
            Create Delivery
          </Button>
        </Card>
      )}

      {/* Delivery History */}
      {deliveryHistory.length > 0 && (
        <div>
          <h3 className="font-semibold mb-3">Recent Deliveries</h3>
          <div className="space-y-2">
            {deliveryHistory.slice(0, 5).map((delivery) => (
              <Card key={delivery.id} className="p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Badge variant={delivery.package_status === 'delivered' ? 'default' : 'destructive'}>
                      {STATUS_LABELS[delivery.package_status]}
                    </Badge>
                    <p className="text-sm mt-1 text-muted-foreground">
                      {new Date(delivery.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <p className="font-medium">₹{delivery.fare}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Tracking Sheet */}
      <Sheet open={!!selectedDelivery} onOpenChange={() => setSelectedDelivery(null)}>
        <SheetContent side="bottom" className="h-[90vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Track Package</SheetTitle>
          </SheetHeader>
          {selectedDelivery && (
            <PackageDeliveryTracking
              delivery={selectedDelivery}
              onClose={() => setSelectedDelivery(null)}
              onRefresh={handleRefresh}
            />
          )}
        </SheetContent>
      </Sheet>

      {/* New Delivery Dialog */}
      <Dialog open={showNewDelivery} onOpenChange={setShowNewDelivery}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Package Delivery</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Pickup Address</Label>
              <Input
                placeholder="Enter pickup address"
                value={newDelivery.pickupAddress}
                onChange={(e) => setNewDelivery({ ...newDelivery, pickupAddress: e.target.value })}
              />
              <p className="text-xs text-muted-foreground mt-1">
                For demo: Use coords 12.9716, 77.5946 (MG Road)
              </p>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Input
                  type="number"
                  step="0.0001"
                  placeholder="Latitude"
                  value={newDelivery.pickupLat || ''}
                  onChange={(e) => setNewDelivery({ ...newDelivery, pickupLat: parseFloat(e.target.value) })}
                />
                <Input
                  type="number"
                  step="0.0001"
                  placeholder="Longitude"
                  value={newDelivery.pickupLng || ''}
                  onChange={(e) => setNewDelivery({ ...newDelivery, pickupLng: parseFloat(e.target.value) })}
                />
              </div>
            </div>

            <div>
              <Label>Delivery Address</Label>
              <Input
                placeholder="Enter delivery address"
                value={newDelivery.deliveryAddress}
                onChange={(e) => setNewDelivery({ ...newDelivery, deliveryAddress: e.target.value })}
              />
              <p className="text-xs text-muted-foreground mt-1">
                For demo: Use coords 12.9352, 77.6245 (Koramangala)
              </p>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Input
                  type="number"
                  step="0.0001"
                  placeholder="Latitude"
                  value={newDelivery.deliveryLat || ''}
                  onChange={(e) => setNewDelivery({ ...newDelivery, deliveryLat: parseFloat(e.target.value) })}
                />
                <Input
                  type="number"
                  step="0.0001"
                  placeholder="Longitude"
                  value={newDelivery.deliveryLng || ''}
                  onChange={(e) => setNewDelivery({ ...newDelivery, deliveryLng: parseFloat(e.target.value) })}
                />
              </div>
            </div>

            <div>
              <Label>Package Weight (kg)</Label>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setNewDelivery({ ...newDelivery, weight: Math.max(0.5, newDelivery.weight - 0.5) })}
                >
                  -
                </Button>
                <span className="w-16 text-center font-bold">{newDelivery.weight} kg</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setNewDelivery({ ...newDelivery, weight: Math.min(50, newDelivery.weight + 0.5) })}
                >
                  +
                </Button>
              </div>
              {bookingPricing?.package && (
                <p className="text-xs text-muted-foreground mt-1">
                  Max {bookingPricing.package.max_weight_kg}kg included. Extra: ₹{bookingPricing.package.extra_weight_charge}/kg
                </p>
              )}
            </div>

            <div>
              <Label>Package Description</Label>
              <Input
                placeholder="e.g., Documents, Electronics, Food"
                value={newDelivery.description}
                onChange={(e) => setNewDelivery({ ...newDelivery, description: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Recipient Name</Label>
                <Input
                  placeholder="Recipient name"
                  value={newDelivery.recipientName}
                  onChange={(e) => setNewDelivery({ ...newDelivery, recipientName: e.target.value })}
                />
              </div>
              <div>
                <Label>Recipient Phone</Label>
                <Input
                  placeholder="10-digit phone"
                  value={newDelivery.recipientPhone}
                  onChange={(e) => setNewDelivery({ ...newDelivery, recipientPhone: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label>Special Instructions</Label>
              <Textarea
                placeholder="Any special handling instructions"
                value={newDelivery.instructions}
                onChange={(e) => setNewDelivery({ ...newDelivery, instructions: e.target.value })}
              />
            </div>

            <Button onClick={estimateFare} variant="outline" className="w-full">
              Get Fare Estimate
            </Button>

            {fareEstimate && (
              <Card className="p-4 bg-muted/50">
                <div className="flex justify-between items-center mb-2">
                  <span>Distance</span>
                  <span className="font-medium">{fareEstimate.distance_km} km</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span>Est. Time</span>
                  <span className="font-medium">{fareEstimate.estimated_time_mins} mins</span>
                </div>
                {fareEstimate.extra_weight_kg > 0 && (
                  <div className="flex justify-between items-center mb-2 text-amber-600">
                    <span>Extra Weight ({fareEstimate.extra_weight_kg}kg)</span>
                    <span className="font-medium">+₹{fareEstimate.extra_weight_charge}</span>
                  </div>
                )}
                <div className="flex justify-between items-center pt-2 border-t">
                  <span className="font-semibold">Total Fare</span>
                  <span className="text-xl font-bold text-primary">₹{fareEstimate.fare}</span>
                </div>
              </Card>
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowNewDelivery(false)}>Cancel</Button>
            <Button onClick={createDelivery} disabled={!fareEstimate}>
              Create Delivery
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PackageDelivery;
